import type { ReactNode } from 'react';
import { OnSurface } from '../../components/ui/surface.js';

/**
 * A register's surface (D278): the approved redesign's list "box" — a white
 * sheet with a hairline border, the filters in a strip across its top and the
 * table flush with its edges, so the sunken header row meets the border.
 *
 * `Panel` pads its body, which insets a table inside a second frame; this is
 * the flush variant a list needs. It is local to the pages that use it until
 * the shared library offers one (reported to the lead, not added there).
 */
export function RegisterPanel({
  toolbar,
  children,
}: {
  /** The list's filters — usually a `FilterBar`. */
  readonly toolbar?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <section className="min-w-0 rounded-large border border-default-200 bg-white">
      <OnSurface>
        {toolbar ? (
          // FilterBar carries its own bottom margin for the page ground; inside
          // the strip the strip's padding replaces it.
          <div className="border-b border-default-200 px-4 py-3 [&>div]:mb-0">{toolbar}</div>
        ) : null}
        <div className="pb-2">{children}</div>
      </OnSurface>
    </section>
  );
}

/**
 * The record bar at the top of a record page (D278): the record's state and its
 * key context, in one strip under the page title. Every item is a fact the page
 * already had; nothing here is computed.
 */
export function RecordStrip({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-gutter gap-y-2 rounded-large border border-default-200 bg-white px-4 py-3">
      {children}
    </div>
  );
}

/** One labelled item of a record strip: a caption over its value. */
export function StripItem({
  label,
  children,
}: {
  /** Already translated. */
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-micro font-semibold text-default-600">{label}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-inline text-body text-default-900">
        {children}
      </div>
    </div>
  );
}
